/**
 * 📈 BiometricTrendChart — 4-week rolling weight chart with PB markers.
 *
 * Renders inside HeroBiometricsCard. Consumes the same `entries` payload
 * from `/api/hero/biometrics?days=28`.
 *
 * Owner canon Feb 18, 2026: "Ship the 4-week rolling weight chart with
 * personal-best markers so the reminder feels rewarding to log."
 *
 * Visuals:
 *   • Sparkline showing per-day weight
 *   • 7-day rolling average overlay (lighter stroke)
 *   • ⭐ PB marker — the lowest weight datapoint in the window
 *   • 🏁 Start marker — first datapoint in the window
 *   • Y-axis min/max labels + delta chip vs starting weight
 *   • Empty state when < 2 datapoints
 */
import { useMemo } from "react";

const W = 320;
const H = 96;
const PAD_X = 26;
const PAD_Y_TOP = 12;
const PAD_Y_BOT = 20;

function rollingAvg(vals, window = 7) {
  if (!vals?.length) return [];
  return vals.map((_, i) => {
    const start = Math.max(0, i - window + 1);
    const slice = vals.slice(start, i + 1).filter((v) => Number.isFinite(v));
    if (!slice.length) return null;
    const sum = slice.reduce((a, b) => a + b, 0);
    return sum / slice.length;
  });
}

export default function BiometricTrendChart({ entries, unit = "kg" }) {
  const data = useMemo(() => {
    const pts = (entries || [])
      .filter((e) => Number.isFinite(Number(e.weight_kg)))
      .map((e) => ({
        date: e.date,
        weight: unit === "lb" ? Number(e.weight_kg) * 2.20462 : Number(e.weight_kg),
      }));
    return pts.slice(-28);   // last 4 weeks
  }, [entries, unit]);

  if (!data || data.length < 2) {
    return (
      <div className="rounded-xl border border-amber-900/40 bg-black/30 p-4 text-center"
           data-testid="biometric-trend-chart-empty">
        <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300/70 mb-1.5">
          📈 4-Week Trend
        </div>
        <div className="text-[11px] text-neutral-500">
          Log at least 2 weigh-ins to unlock your trend chart.
        </div>
      </div>
    );
  }

  const vals = data.map((d) => d.weight);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = Math.max(0.1, max - min);
  const yPad = range * 0.15;           // breathing room above/below
  const yMin = min - yPad;
  const yMax = max + yPad;
  const yRange = yMax - yMin;

  const xFor = (i) => PAD_X + (i * (W - 2 * PAD_X)) / Math.max(1, data.length - 1);
  const yFor = (v) => H - PAD_Y_BOT - ((v - yMin) / yRange) * (H - PAD_Y_TOP - PAD_Y_BOT);

  // Weight polyline
  const linePts = data.map((d, i) => `${xFor(i)},${yFor(d.weight)}`).join(" ");

  // 7-day rolling average
  const avg = rollingAvg(vals, 7);
  const avgPts = avg
    .map((v, i) => (v == null ? null : `${xFor(i)},${yFor(v)}`))
    .filter(Boolean)
    .join(" ");

  // PB = min weight (fitness convention: lower = better cut PB). Ties → most recent.
  let pbIdx = 0;
  for (let i = 1; i < vals.length; i++) if (vals[i] <= vals[pbIdx]) pbIdx = i;
  const pb = data[pbIdx];

  // Start point
  const start = data[0];
  const startingWeight = start.weight;
  const latest = data[data.length - 1];
  const delta = latest.weight - startingWeight;
  const deltaColor = delta < 0 ? "text-emerald-400"
                   : delta > 0 ? "text-rose-300"
                   : "text-neutral-400";
  const deltaSign  = delta > 0 ? "+" : "";

  const fmt = (v) => `${v.toFixed(1)}${unit === "lb" ? "" : ""}`;

  return (
    <div className="rounded-xl border border-amber-900/40 bg-gradient-to-b from-black/40 to-black/20 p-3"
         data-testid="biometric-trend-chart">
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300/80 font-bold">
          📈 4-Week Trend
        </div>
        <div className="flex items-center gap-2 text-[10px]">
          <span className="text-neutral-500">Δ start</span>
          <span className={`font-bold ${deltaColor}`}>
            {deltaSign}{delta.toFixed(1)} {unit}
          </span>
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        <defs>
          <linearGradient id="wt-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#F59E0B" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#F59E0B" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Baseline min/max grid lines */}
        <line x1={PAD_X} y1={yFor(max)} x2={W - PAD_X} y2={yFor(max)}
              stroke="#404040" strokeWidth="0.5" strokeDasharray="2,3" />
        <line x1={PAD_X} y1={yFor(min)} x2={W - PAD_X} y2={yFor(min)}
              stroke="#404040" strokeWidth="0.5" strokeDasharray="2,3" />

        {/* Area fill under weight line */}
        <polygon
          fill="url(#wt-area)"
          points={`${PAD_X},${H - PAD_Y_BOT} ${linePts} ${W - PAD_X},${H - PAD_Y_BOT}`}
        />

        {/* 7-day rolling average */}
        {avgPts && (
          <polyline
            fill="none"
            stroke="#A78BFA"
            strokeWidth="1.2"
            strokeDasharray="3,3"
            points={avgPts}
            opacity="0.85"
          />
        )}

        {/* Weight line */}
        <polyline
          fill="none"
          stroke="#F59E0B"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={linePts}
        />

        {/* Data dots */}
        {data.map((d, i) => (
          <circle
            key={`pt-${d.date}`}
            cx={xFor(i)} cy={yFor(d.weight)}
            r={i === pbIdx ? 4 : (i === 0 || i === data.length - 1) ? 3 : 1.6}
            fill={i === pbIdx ? "#FCD34D" : "#F59E0B"}
            stroke={i === pbIdx ? "#000" : "none"}
            strokeWidth="0.8"
          />
        ))}

        {/* PB marker star */}
        <text x={xFor(pbIdx)} y={yFor(pb.weight) - 8}
              fill="#FCD34D" fontSize="10" textAnchor="middle" fontWeight="900">
          ⭐
        </text>

        {/* Y axis labels */}
        <text x={4} y={yFor(max) + 3} fill="#a3a3a3" fontSize="9">{fmt(max)}</text>
        <text x={4} y={yFor(min) + 3} fill="#a3a3a3" fontSize="9">{fmt(min)}</text>

        {/* X axis: start + latest date */}
        <text x={xFor(0)} y={H - 4} fill="#737373" fontSize="8" textAnchor="start">
          {String(start.date).slice(5)}
        </text>
        <text x={xFor(data.length - 1)} y={H - 4} fill="#737373" fontSize="8" textAnchor="end">
          {String(latest.date).slice(5)}
        </text>
      </svg>

      <div className="mt-2 grid grid-cols-3 gap-2 text-[10px]">
        <div className="rounded bg-black/30 px-2 py-1.5 border border-neutral-800">
          <div className="text-neutral-500 uppercase tracking-wider text-[9px]">🏁 Start</div>
          <div className="text-neutral-200 font-bold">{fmt(startingWeight)} {unit}</div>
          <div className="text-neutral-600 text-[9px]">{String(start.date).slice(5)}</div>
        </div>
        <div className="rounded bg-amber-950/30 px-2 py-1.5 border border-amber-800/50"
             data-testid="biometric-trend-pb">
          <div className="text-amber-300/80 uppercase tracking-wider text-[9px]">⭐ PB</div>
          <div className="text-amber-200 font-bold">{fmt(pb.weight)} {unit}</div>
          <div className="text-amber-500/60 text-[9px]">{String(pb.date).slice(5)}</div>
        </div>
        <div className="rounded bg-black/30 px-2 py-1.5 border border-neutral-800">
          <div className="text-neutral-500 uppercase tracking-wider text-[9px]">📍 Latest</div>
          <div className="text-neutral-200 font-bold">{fmt(latest.weight)} {unit}</div>
          <div className="text-neutral-600 text-[9px]">{String(latest.date).slice(5)}</div>
        </div>
      </div>

      <div className="mt-1.5 flex items-center justify-center gap-3 text-[9px] text-neutral-500">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-[2px] bg-amber-500 rounded"></span> daily
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 border-t border-dashed border-violet-400"></span> 7-day avg
        </span>
        <span>{data.length} log{data.length === 1 ? "" : "s"} in {Math.min(28, data.length)}d</span>
      </div>
    </div>
  );
}
